/**
 * Admin — Honor journey page.
 *
 * Read-only view of user achievement milestones.
 * Filterable by user_id and milestone type.
 */
import { useState } from 'react';
import { Input, Select, Space, Tag, Modal, Button } from 'antd';
import { EyeOutlined } from '@ant-design/icons';
import { DataTable } from '../../components/DataTable';

const MILESTONE_COLORS: Record<string, string> = {
  level_up: 'gold',
  donation_tier: 'green',
  trade_count: 'blue',
  first_recharge: 'cyan',
  first_trade: 'purple',
  referral_count: 'magenta',
};

const MILESTONE_OPTIONS = [
  { value: 'level_up', label: '等级提升' },
  { value: 'donation_tier', label: '捐赠等级' },
  { value: 'trade_count', label: '交易里程碑' },
  { value: 'first_recharge', label: '首次充值' },
  { value: 'first_trade', label: '首次交易' },
  { value: 'referral_count', label: '推荐里程碑' },
];

interface HonorMilestone {
  id: number;
  user_id: number;
  milestone: string;
  title: string;
  description: string | null;
  value: string | null;
  badge: string | null;
  create_time: string;
}

export function HonorJourneyPage() {
  const [userIdFilter, setUserIdFilter] = useState<number | undefined>(undefined);
  const [milestoneFilter, setMilestoneFilter] = useState<string | undefined>(undefined);
  const [detailModal, setDetailModal] = useState<HonorMilestone | null>(null);

  const columns = [
    { title: 'ID', dataIndex: 'id', key: 'id', width: 60 },
    { title: '用户ID', dataIndex: 'user_id', key: 'user_id', width: 80 },
    {
      title: '里程碑',
      dataIndex: 'milestone',
      key: 'milestone',
      width: 120,
      render: (v: string) => (
        <Tag color={MILESTONE_COLORS[v] ?? 'default'}>{v}</Tag>
      ),
    },
    { title: '标题', dataIndex: 'title', key: 'title', width: 200, ellipsis: true },
    { title: '值', dataIndex: 'value', key: 'value', width: 100,
      render: (v: string | null) => v ?? '-' },
    { title: '徽章', dataIndex: 'badge', key: 'badge', width: 80,
      render: (v: string | null) => v ?? '-' },
    {
      title: '时间',
      dataIndex: 'create_time',
      key: 'create_time',
      width: 170,
      render: (v: string) => (v ? new Date(v).toLocaleString('zh-CN') : '-'),
    },
    {
      title: '操作',
      key: 'actions',
      width: 80,
      render: (_: unknown, row: HonorMilestone) => (
        <Button
          type="link"
          size="small"
          icon={<EyeOutlined />}
          onClick={() => setDetailModal(row)}
        >
          详情
        </Button>
      ),
    },
  ];

  return (
    <div>
      <Space style={{ marginBottom: 16 }}>
        <Input
          style={{ width: 120 }}
          placeholder="用户ID"
          type="number"
          value={userIdFilter ?? ''}
          onChange={(e) => {
            const v = e.target.value ? Number(e.target.value) : undefined;
            setUserIdFilter(v);
          }}
          allowClear
        />
        <Select
          style={{ width: 160 }}
          placeholder="里程碑类型"
          allowClear
          value={milestoneFilter}
          onChange={(v) => setMilestoneFilter(v)}
          options={MILESTONE_OPTIONS}
        />
      </Space>

      <DataTable<HonorMilestone>
        queryKey={['honor-journey-list']}
        endpoint="/api/admin/honor"
        extraParams={{
          ...(userIdFilter ? { user_id: userIdFilter } : {}),
          ...(milestoneFilter ? { milestone: milestoneFilter } : {}),
        }}
        columns={columns}
        rowKey="id"
      />

      <Modal
        title={detailModal ? `里程碑详情 #${detailModal.id}` : ''}
        open={!!detailModal}
        onCancel={() => setDetailModal(null)}
        footer={null}
        width={520}
      >
        {detailModal && (
          <div>
            <p><strong>用户ID:</strong> {detailModal.user_id}</p>
            <p><strong>里程碑:</strong> <Tag color={MILESTONE_COLORS[detailModal.milestone] ?? 'default'}>{detailModal.milestone}</Tag></p>
            <p><strong>标题:</strong> {detailModal.title}</p>
            <p><strong>描述:</strong> {detailModal.description ?? '-'}</p>
            <p><strong>值:</strong> {detailModal.value ?? '-'}</p>
            <p><strong>徽章:</strong> {detailModal.badge ?? '-'}</p>
            <p><strong>时间:</strong> {new Date(detailModal.create_time).toLocaleString('zh-CN')}</p>
          </div>
        )}
      </Modal>
    </div>
  );
}

export default HonorJourneyPage;
